import { useCallback, useEffect, useId, useState } from "react";
import Box from "@mui/material/Box";
import ButtonMui from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Button from "../Buttons/Button";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  AGENCY_ACCOUNT_LINKING_CARD_BODY,
  AGENCY_ACCOUNT_LINKING_CARD_TITLE,
  AGENCY_ACCOUNT_LINKING_ENABLE,
  AGENCY_ACCOUNT_LINKING_NOT_NOW,
} from "../../i18n/keysTranslations";
import { isAgencyLinkingAlertVisible } from "../../utils/agencyAccountLinking";
import {
  dismissAgencyLinkingPromptForSession,
  isAgencyLinkingPromptDismissedForSession,
} from "../../agencyAccountLinking/promptSession";
import useAgencyAccountLinkingEnabled from "../../hooks/useAgencyAccountLinkingEnabled";
import useAgencyAccountLinking from "../../hooks/useAgencyAccountLinking";
import ModalAgencyClientAccounts from "./ModalAgencyClientAccounts";

const AgencyLinkingTipCard = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const titleID = useId();
  const enabled = useAgencyAccountLinkingEnabled();
  const { linkAccounts, linking, prompt, completed, hidden, refreshPrompt } =
    useAgencyAccountLinking();
  const { agencyID } = dispatch(getPathDatabaseByRole());
  const [dismissedForSession, setDismissedForSession] = useState(() =>
    isAgencyLinkingPromptDismissedForSession({ agencyID })
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetAccounts, setSheetAccounts] = useState([]);

  useEffect(() => {
    setDismissedForSession(
      isAgencyLinkingPromptDismissedForSession({ agencyID })
    );
  }, [agencyID]);

  const onNotNow = useCallback(() => {
    dismissAgencyLinkingPromptForSession({ agencyID });
    setDismissedForSession(
      isAgencyLinkingPromptDismissedForSession({ agencyID })
    );
    setSheetOpen(false);
  }, [agencyID]);

  const onEnable = () => {
    if (!prompt?.accounts?.length || linking) return;
    setSheetAccounts(prompt.accounts);
    setSheetOpen(true);
  };

  const visible = isAgencyLinkingAlertVisible({
    enabled,
    dismissedForSession,
    hidden,
    completed,
    prompt,
  });

  if (!visible && !sheetOpen) return null;

  return (
    <>
      {visible ? (
        <Box
          component="section"
          aria-labelledby={titleID}
          sx={{
            mx: { xs: 2, sm: 3 },
            mt: 3,
            px: { xs: 2, sm: 2.5 },
            py: 2,
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            borderRadius: "16px",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
          }}
        >
          <Box
            sx={{ display: "flex", gap: 1.25, minWidth: 0, flex: "1 1 300px" }}
          >
            <AutoAwesomeIcon
              aria-hidden
              sx={{
                fontSize: 18,
                color: "primary.main",
                mt: 0.3,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                id={titleID}
                component="h2"
                sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.35 }}
              >
                {t(AGENCY_ACCOUNT_LINKING_CARD_TITLE)}
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 13.5,
                  lineHeight: 1.45,
                  color: "text.secondary",
                  maxWidth: 640,
                }}
              >
                {t(AGENCY_ACCOUNT_LINKING_CARD_BODY)}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
              ml: "auto",
            }}
          >
            <ButtonMui
              onClick={onNotNow}
              disabled={linking}
              sx={{
                textTransform: "none",
                color: "text.secondary",
                fontWeight: 500,
                minWidth: 0,
              }}
            >
              {t(AGENCY_ACCOUNT_LINKING_NOT_NOW)}
            </ButtonMui>
            <Button
              color="primary"
              loading={linking}
              ariaLabel={t(AGENCY_ACCOUNT_LINKING_ENABLE)}
              onClick={onEnable}
              sx={{ width: "auto", minWidth: 104 }}
            >
              {t(AGENCY_ACCOUNT_LINKING_ENABLE)}
            </Button>
          </Box>
        </Box>
      ) : null}
      {sheetOpen ? (
        <ModalAgencyClientAccounts
          modalOpen={sheetOpen}
          accounts={sheetAccounts}
          onClose={() => {
            setSheetOpen(false);
            refreshPrompt?.();
          }}
          onLinkAccounts={linkAccounts}
        />
      ) : null}
    </>
  );
};

export default AgencyLinkingTipCard;
